import { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { isObject } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import useFormData from '@gef-ui/form-core/hooks/state/useFormData';
import { getLabelMessage } from '@gef-ui/localization/messages';

import { ComponentLoader, Dialog } from 'components';
import Panel from 'components/atoms/GefMui/Panel';
import PaperWithToolbar from 'components/atoms/GefMui/PaperWithToolbar';
import Subheader from 'components/atoms/GefMui/Subheader';
import PAGES from 'constants/pages';
import dialog from 'features/common/dialog';
import fetch from 'features/common/fetch';
import { subheaders } from 'features/common/Subheader/constants';
import { useRenderSubheaderTitle } from 'features/common/Subheader/hooks';
import { fetchCampaignDetail } from 'features/views/campaignManagement/campaignDetail/actions';
import { getCampaignDetail } from 'features/views/campaignManagement/campaignDetail/selectors';
import contactReleasing from 'features/views/campaignManagement/contactReleasing';
import contacts from 'features/views/campaignManagement/contacts';
import {
	possiblyResetRelatedSections,
	prefillFormWithPreviousData,
} from 'features/views/campaignManagement/contacts/actions';

import ContactStatePills from './components/ContactStatePills';
import Form from './components/Form';
import Progress from './components/Progress';
import Table from './components/Table';
import Toolbar from './components/Toolbar';

const { dialogs } = contactReleasing.constants;

export const CampaignManagement = ({ campaignId }) => {
	const dispatch = useDispatch();
	const { pathname } = useLocation();

	const formData = useFormData();
	const formIsInitiated = isObject(formData);

	const contactsFetched = useSelector(fetch.selectors.getWasFetched(fetch.constants.urls.contactList));
	const campaignDetail = useSelector(getCampaignDetail);

	const { fullName, contactDataColumns } = campaignDetail || {};

	useEffect(() => {
		if (formIsInitiated) {
			if (contactsFetched) {
				dispatch(contacts.actions.reload());
				dispatch(prefillFormWithPreviousData());
			} else {
				dispatch(fetchCampaignDetail(campaignId));
				dispatch(contacts.actions.startupContacts(campaignId));
			}
		}
		dispatch(contactReleasing.actions.runCheckForProgress());
	}, [dispatch, campaignId, contactsFetched, formIsInitiated]);

	useEffect(
		() => () => {
			dispatch(possiblyResetRelatedSections());
			dispatch(dialog.actions.clear());
		},
		[dispatch, pathname],
	);

	const handleReload = useCallback(() => {
		dispatch(fetchCampaignDetail(campaignId));
	}, [dispatch, campaignId]);

	const subheaderTitle = useRenderSubheaderTitle({
		pageTitle: fullName || campaignId,
		backButton: true,
	});

	const floatingDialogs = (
		<>
			<Dialog mui name={dialogs.NO_SELLER_SELECTED}>
				{getLabelMessage('campaignManagement:noSellerSelected')}
			</Dialog>
			<Dialog mui name={dialogs.NO_CONTACT_SELECTED}>
				{getLabelMessage('campaignManagement:noSelectContact')}
			</Dialog>
			<Dialog mui name={dialogs.NO_CONTACTS_AVAILABLE}>
				{getLabelMessage('campaignManagement:noContactsAvailable')}
			</Dialog>
			<Dialog mui name={dialogs.NO_PROCESS_SELECTED}>
				{getLabelMessage('campaignManagement:noProcessSelected')}
			</Dialog>
			<Dialog mui name={dialogs.CONTACT_PROCESSING_ERROR}>
				{getLabelMessage('campaignManagement:contactProcessingError')}
			</Dialog>
			<Progress />
		</>
	);

	return (
		<ComponentLoader fetchStorePath={fetch.constants.urls.campaignDetail} onReload={handleReload}>
			<Panel
				screenId={PAGES.campaignManagement.id}
				screenName={PAGES.campaignManagement.name}
				floatingDialogs={floatingDialogs}
			>
				<Paper sx={{ p: 3, borderRadius: 2 }}>
					<Stack spacing={2}>
						<Subheader title={subheaderTitle} />
						<ContactStatePills />
					</Stack>
				</Paper>
				<PaperWithToolbar
					toggledElement={<Form />}
					toolbar={<Toolbar />}
					collapsibleProps={{ id: subheaders.campaignManagement, initiallyOpen: true }}
				>
					<Table contactDataColumns={contactDataColumns} />
				</PaperWithToolbar>
			</Panel>
		</ComponentLoader>
	);
};

CampaignManagement.propTypes = {
	campaignId: PropTypes.string,
};

export default CampaignManagement;
